import { useCallback, useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import axios from '../config/axios';
import { getApiUrl, getWebSocketUrl } from '../config/environment';
import useNotifications from './useNotifications';
import { useTeamChatSoundEnabled } from './useTeamChatSoundEnabled';
import { playTeamChatPip, primeNotificationSound } from '../utils/notificationSound';
import { isUserViewingPage } from '../pages/TeamChat/teamChatUtils';

const REFRESH_EVENT = 'waiting-room:menu-badge-refresh';

export function dispatchWaitingRoomMenuBadgeRefresh() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(REFRESH_EVENT));
}

/**
 * Contador da sala de espera no menu lateral (profissionais com vínculo clínico):
 * polling leve + WebSocket `waiting_room:updated`, som e notificação como o chat da equipe.
 */
export function useWaitingRoomMenuAlerts(options: {
  enabled: boolean;
  professionalId: string | null | undefined;
  unitId: string | null | undefined;
  onCountChange: (count: number) => void;
  /** Evita som ao abrir a própria página da fila. */
  suppressAlerts?: boolean;
}) {
  const { enabled, professionalId, unitId, onCountChange, suppressAlerts = false } = options;
  const { showNotification } = useNotifications();
  const { enabledRef: soundEnabledRef, volumeRef: soundVolumeRef } = useTeamChatSoundEnabled();

  const lastCountRef = useRef(0);
  const onCountChangeRef = useRef(onCountChange);
  onCountChangeRef.current = onCountChange;

  const fetchCount = useCallback(async () => {
    if (!enabled || !professionalId) {
      lastCountRef.current = 0;
      onCountChangeRef.current(0);
      return 0;
    }
    try {
      const res = await axios.get(getApiUrl('/clinic/waiting-room/menu-badge'));
      const next = Number(res.data?.data?.count) || 0;
      const prev = lastCountRef.current;
      const increased = next > prev;

      if (increased && !suppressAlerts) {
        if (soundEnabledRef.current) {
          playTeamChatPip(soundVolumeRef.current);
        }
        if (!isUserViewingPage()) {
          void showNotification({
            title: 'Sala de espera',
            body:
              next - prev === 1
                ? 'Novo paciente aguardando atendimento.'
                : `${next - prev} pacientes entraram na fila.`,
            tag: 'waiting-room-menu',
            data: { path: '/clinic/waiting-room' },
          });
        }
      }

      lastCountRef.current = next;
      onCountChangeRef.current(next);
      return next;
    } catch {
      return lastCountRef.current;
    }
  }, [
    enabled,
    professionalId,
    suppressAlerts,
    showNotification,
    soundEnabledRef,
    soundVolumeRef,
  ]);

  const fetchCountRef = useRef(fetchCount);
  fetchCountRef.current = fetchCount;

  useEffect(() => {
    if (!enabled || !professionalId) {
      lastCountRef.current = 0;
      onCountChangeRef.current(0);
      return undefined;
    }

    primeNotificationSound();
    void fetchCountRef.current();

    const interval = window.setInterval(() => {
      if (document.hidden) return;
      void fetchCountRef.current();
    }, 45000);

    const onRefresh = () => void fetchCountRef.current();
    window.addEventListener(REFRESH_EVENT, onRefresh);
    window.addEventListener('focus', onRefresh);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void fetchCountRef.current();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('unit:changed', onRefresh);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener(REFRESH_EVENT, onRefresh);
      window.removeEventListener('focus', onRefresh);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('unit:changed', onRefresh);
    };
  }, [enabled, professionalId, unitId]);

  useEffect(() => {
    if (suppressAlerts) {
      void fetchCountRef.current();
    }
  }, [suppressAlerts]);

  useEffect(() => {
    if (!enabled || !professionalId) return undefined;

    const wsUrl = getWebSocketUrl();
    if (!wsUrl?.trim()) return undefined;

    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (!token) return undefined;

    let debounce: number | null = null;
    const schedule = () => {
      if (debounce) window.clearTimeout(debounce);
      debounce = window.setTimeout(() => {
        debounce = null;
        void fetchCountRef.current();
      }, 280);
    };

    const socket: Socket = io(wsUrl, {
      transports: ['websocket', 'polling'],
      timeout: 20000,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: Infinity,
      auth: { token },
    });

    socket.on('connect', schedule);
    socket.on('reconnect', schedule);
    socket.on('waiting_room:updated', schedule);

    return () => {
      socket.off('connect', schedule);
      socket.off('reconnect', schedule);
      socket.off('waiting_room:updated', schedule);
      socket.disconnect();
      if (debounce) window.clearTimeout(debounce);
    };
  }, [enabled, professionalId, unitId]);
}

export default useWaitingRoomMenuAlerts;
